import {
	AntDesign,
	FontAwesome,
	Ionicons,
	MaterialCommunityIcons
} from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/core";
import React, {
	createRef,
	Fragment,
	memo,
	useEffect,
	useRef,
	useState
} from "react";
import {
	ActivityIndicator,
	Image,
	LayoutAnimation,
	StyleSheet,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Avatar, Button, Tile } from "react-native-elements";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchGetAdDetails } from "../actions/ad";
import {
	fetchDeleteLiveEvent,
	fetchFlagInappropriateContent,
	fetchInterested,
	fetchUninterested
} from "../actions/live";
import { SET_DROP_DOWN_ALERT_WARN, SHARE_POST } from "../actions/types";
import { getImageUri } from "../assets/Images";
import ActionButton from "../components/actionButton/ActionButton";
import ConfirmationModal from "../components/ConfirmationModal";
import {
	GROUP_ID_ACTIVITY,
	GROUP_ID_GOODDEAL_ACTIVITY,
	GROUP_ID_NEW_USER,
	GROUP_ID_PROPOSAL,
	GROUP_ID_PUBLISH,
	GROUP_ID_TEMPLATE,
	GROUP_ID_WEATHER,
	ROLE_GUEST,
	ROLE_ADMINISTRATOR,
	ROLE_WEBMASTER
} from "../config/constants";
import { getLiveEventSelector } from "../selectors";
import { getTranslatedProperty, t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	COLOR5,
	DARKGREY_COLOR,
	ORANGE_COLOR,
	SCREEN_HEIGHT,
	SCREEN_WIDTH
} from "../styles/commonStyles";
import { formatDate } from "../utils/DateUtils";
import { openBrowser } from "../utils/UrlUtil";
import LiveHeader from "./LiveHeader";
import { getAvatarSource } from "../utils/AvatarUtil";

// Helper function to check if a URI is a PDF
const isPdfUri = uri => {
	if (!uri) return false;
	const uriLower = uri.toLowerCase();
	return (
		uriLower.endsWith(".pdf") ||
		uriLower.includes("application/pdf") ||
		uriLower.startsWith("data:application/pdf")
	);
};

// Helper function to extract PDF name from URI
const extractPdfNameFromUri = uri => {
	if (!uri) return null;

	// If it's a data URI, try to extract from it
	if (uri.startsWith("data:application/pdf")) {
		// Could contain filename in the data URI, but usually doesn't
		// Check if there's a filename parameter
		const match = uri.match(/filename=([^;]+)/i);
		if (match) {
			try {
				return decodeURIComponent(match[1]).replace(/\.pdf$/i, "");
			} catch {
				return match[1].replace(/\.pdf$/i, "");
			}
		}
		return null;
	}

	// Extract filename from URI path
	try {
		// Handle both regular URLs and file:// URIs
		let path = uri;
		if (uri.includes("://")) {
			// Remove protocol and domain
			const urlParts = uri.split("://");
			if (urlParts.length > 1) {
				path = urlParts[1].split("/").slice(1).join("/");
			}
		}

		const urlParts = path.split("/");
		const filename = urlParts[urlParts.length - 1];

		// Remove query parameters and hash if any
		const cleanFilename = filename.split("?")[0].split("#")[0];

		// Remove .pdf extension and decode if needed
		if (cleanFilename.toLowerCase().endsWith(".pdf")) {
			const nameWithoutExt = cleanFilename.slice(0, -4);
			// Try to decode URI component
			try {
				return decodeURIComponent(nameWithoutExt);
			} catch {
				return nameWithoutExt;
			}
		}

		// If no .pdf extension but URI suggests it's a PDF, try to use the filename anyway
		if (isPdfUri(uri) && cleanFilename) {
			try {
				return decodeURIComponent(cleanFilename);
			} catch {
				return cleanFilename;
			}
		}

		return null;
	} catch {
		return null;
	}
};

const LiveEvent = ({ item, isDetails = false, previewTemplate }) => {
	const dispatch = useDispatch();
	const navigation = useNavigation();
	const isMounted = createRef(null);
	const confirmationModal = useRef();

	const [fetchingDelete, setFetchingDelete] = useState(false);
	const [fetchingUpdate, setFetchingUpdate] = useState(false);
	const [fetchingFlagContent] = useState(false);
	const [imageHeight, setImageHeight] = useState(0);
	const [imageWidth, setImageWidth] = useState(0);

	const {
		interestedusers,
		templates,
		locations,
		equipments,
		templateDates,
		templateTimes,
		user,
		inLiveScreen,
		isFetching,
		ad,
		users
	} = useSelector(getLiveEventSelector, shallowEqual);

	const {
		templateDetails,
		groupId: eventGroupId,
		templateId: eventTemplateId,
		goodDealId: eventGoodDealId
	} = item?.eventId || {};

	const {
		event = templateDetails,
		groupId = eventGroupId,
		templateId = eventTemplateId,
		goodDealId: goodDeal = eventGoodDealId,
		eventId,
		_id,
		userId,
		dateAdded: date,
		interestedUsersIds
	} = item || {};

	const template = previewTemplate || templates[templateId];

	let {
		url,
		goodDealId,
		title: eventTitle,
		subtype,
		photoUri,
		description: eventDescription,
		youtubeVideoId,
		picto,
		startDate
	} = (template ?? eventId ?? event) || {};

	const isMine = Boolean(user && user._id === userId);
	const canEditPublish = Boolean(
		eventId?._id &&
			(isMine ||
				user?.role === ROLE_WEBMASTER ||
				user?.role === ROLE_ADMINISTRATOR)
	);

	const usersanswers = {};
	if (interestedUsersIds) {
		for (const interestedUserId of interestedUsersIds) {
			if (interestedusers[interestedUserId]) {
				usersanswers[interestedUserId] = interestedusers[interestedUserId];
			} else if (interestedUserId === user._id) {
				usersanswers[interestedUserId] = user;
			}
		}
	}

	let {
		bgWhite,
		red,
		fontBold,
		font,
		fs12,
		p10,
		ph10,
		pt10,
		mt5,
		mt10,
		shadowGrey,
		mv5,
		mb5,
		row,
		flex1,
		mb10,
		darkgrey,
		color2,
		rounded10,
		uppercase,
		opacity0,
		rowReverse,
		alignItemsCenter,
		alignSelfCenter,
		borderBottomColor5,
		borderRightColor5,
		ml10,
		overflowHidden
	} = commonStyles;

	useEffect(() => {
		(async () => {
			if (groupId === GROUP_ID_GOODDEAL_ACTIVITY && goodDeal) {
				photoUri = goodDeal.photoUri;
			}
			isMounted.current = true;

			if (photoUri && !youtubeVideoId && !isPdfUri(photoUri)) {
				getImageSize(photoUri, isDetails);
			}
		})();
		return () => (isMounted.current = false);
	}, []);

	useEffect(() => {
		if (ad && inLiveScreen) {
			const { cityUri, customUri: adUri } = ad;
			navigation.navigate("AdScreen", {
				cityUri,
				adUri
			});
		}
	}, [ad]);

	const usePrevious = value => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		});
		return ref.current;
	};

	const usersAnswersLength = Object.keys(usersanswers).length;
	const prevUsersAnswersLength = usePrevious(usersAnswersLength);

	useEffect(() => {
		if (
			isDetails &&
			photoUri &&
			!imageHeight &&
			!youtubeVideoId &&
			!isPdfUri(photoUri)
		) {
			getImageSize(photoUri, isDetails);
		}
	}, [isDetails, photoUri, imageHeight, youtubeVideoId]);

	useEffect(() => {
		if (usersAnswersLength !== prevUsersAnswersLength) {
			setFetchingUpdate(false);
		}
	}, [usersAnswersLength]);

	const getTitle = () => {
		const isInterested = Boolean(user && usersanswers[user._id]);

		let title = "";
		if (isInterested) {
			if (
				groupId === GROUP_ID_ACTIVITY ||
				groupId === GROUP_ID_PROPOSAL ||
				groupId === GROUP_ID_GOODDEAL_ACTIVITY
			) {
				title = t("button:removeanswer");
			} else if (groupId === GROUP_ID_NEW_USER) {
				title = t("button:removewelcome");
			} else if (groupId === GROUP_ID_PUBLISH) {
				title = t("button:dislike");
			} else if (user && user.gender === "F") {
				title = t("button:notinterested_F");
			} else {
				title = t("button:notinterested_M");
			}
		} else {
			if (
				groupId === GROUP_ID_ACTIVITY ||
				groupId === GROUP_ID_PROPOSAL ||
				groupId === GROUP_ID_GOODDEAL_ACTIVITY
			) {
				title = t("button:answerme");
			} else if (groupId === GROUP_ID_NEW_USER) {
				title = t("button:welcome");
			} else if (groupId === GROUP_ID_PUBLISH) {
				title = t("button:like");
			} else if (user && user.gender === "F") {
				title = t("button:interested_F");
			} else {
				title = t("button:interested_M");
			}
		}
		return title;
	};

	const flagContent = () => {
		if (eventId) {
			dispatch(fetchFlagInappropriateContent(eventId._id));
			confirmationModal.current?.close();
		}
	};

	const renderInterestedUsers = () => {
		const components = [];
		for (const interestedUser of Object.values(usersanswers)) {
			components.push(
				<TouchableOpacity
					key={interestedUser._id}
					onPress={() =>
						navigation.navigate("ProfileScreen", {
							userId: interestedUser._id
						})
					}
					disabled={!user || user._id === interestedUser._id}
				>
					<Avatar
						rounded
						size={30}
						source={getAvatarSource(
							interestedUser.photoUri,
							interestedUser.gender
						)}
						title={interestedUser.firstname.toUpperCase().substr(0, 2)}
						activeOpacity={0.7}
						containerStyle={ml10}
					/>
				</TouchableOpacity>
			);
		}
		return components;
	};

	const renderButtons = () => {
		const isInterested = Boolean(
			usersanswers && user && usersanswers[user._id]
		);
		if (groupId === GROUP_ID_GOODDEAL_ACTIVITY && goodDeal) {
			goodDealId = goodDeal._id;
		}

		// construct event to share data
		const eventObj = {
			isEvent: Boolean(event),
			eventId: _id,
			date,
			title: eventTitle,
			photoUri,
			description: eventDescription,
			youtubeVideoId
		};

		let componentButton = null;
		switch (groupId) {
			case GROUP_ID_WEATHER:
				componentButton = (
					<Fragment>
						<ActionButton
							buttonColor={COLOR5}
							renderIcon={() => (
								<MaterialCommunityIcons
									name="dots-horizontal"
									type="material-community"
									size={40}
									color="white"
								/>
							)}
							verticalOrientation="down"
							size={44}
							offsetX={18}
							offsetY={18}
							degrees={90}
							spacing={10}
						>
							<ActionButton.Item
								buttonColor={COLOR2}
								title={t("button:forecast")}
								onPress={() => navigation.navigate("MeteoTab")}
								textStyle={font}
							>
								<MaterialCommunityIcons
									name="weather-partly-cloudy"
									size={34}
									color="white"
								/>
							</ActionButton.Item>
						</ActionButton>
					</Fragment>
				);
				break;

			case GROUP_ID_PUBLISH:
				componentButton = (
					<Fragment>
						<ActionButton
							buttonColor={COLOR5}
							renderIcon={() => (
								<MaterialCommunityIcons
									name="dots-horizontal"
									size={40}
									color="white"
								/>
							)}
							verticalOrientation="down"
							size={44}
							offsetX={18}
							offsetY={18}
							degrees={90}
							spacing={10}
							// autoInactive={false}
						>
							{Boolean(url) && url.trim().length > 0 && (
								<ActionButton.Item
									buttonColor={COLOR2}
									title={t("button:more")}
									onPress={() => openBrowser(dispatch, url)}
									textStyle={font}
								>
									<MaterialCommunityIcons name="web" size={34} color="white" />
								</ActionButton.Item>
							)}
							{Boolean(goodDealId) && (
								<ActionButton.Item
									buttonColor={COLOR2}
									title={t("button:more")}
									onPress={() =>
										dispatch(fetchGetAdDetails({ id: goodDealId }))
									}
									disabled={isFetching}
									textStyle={font}
									textHeight={24}
								>
									{isFetching ? (
										<ActivityIndicator size="small" color="white" />
									) : (
										<MaterialCommunityIcons
											name="dots-horizontal"
											type="material-community"
											size={34}
											color="white"
										/>
									)}
								</ActionButton.Item>
							)}
							<ActionButton.Item
								buttonColor={COLOR2}
								title={t("button:share")}
								onPress={() =>
									dispatch({
										type: SHARE_POST,
										event: eventObj
									})
								}
								textStyle={font}
								textHeight={24}
							>
								<FontAwesome name="share-alt" size={30} color="white" />
							</ActionButton.Item>
							{canEditPublish && (
								<ActionButton.Item
									buttonColor={COLOR2}
									title={t("button:update")}
									onPress={() =>
										navigation.navigate("PostScreen", {
											editEventId: eventId?._id,
											editLiveEventId: _id,
											editData: {
												title: eventTitle,
												description: eventDescription,
												postDescription,
												photoUri,
												youtubeVideoId,
												url,
												lang: eventId?.lang
											}
										})
									}
									textStyle={font}
									textHeight={24}
								>
									<MaterialCommunityIcons
										name="pencil"
										size={30}
										color="white"
									/>
								</ActionButton.Item>
							)}
							{isMine || user?.role === ROLE_WEBMASTER ? (
								<ActionButton.Item
									buttonColor="red"
									title={t("button:delete")}
									onPress={() => {
										isMounted.current && setFetchingDelete(true);
										dispatch(fetchDeleteLiveEvent(_id));
										if (isDetails) {
											navigation.goBack();
										}
									}}
									textStyle={font}
									textHeight={24}
									disabled={fetchingDelete}
								>
									{fetchingDelete ? (
										<ActivityIndicator size="small" color="white" />
									) : (
										<MaterialCommunityIcons
											name={"delete-forever"}
											size={34}
											color="white"
										/>
									)}
								</ActionButton.Item>
							) : (
								Boolean(isDetails && user) &&
								user.role !== ROLE_GUEST && (
									<ActionButton.Item
										buttonColor={COLOR2}
										title={t("button:flagcontent")}
										onPress={() => {
											LayoutAnimation.easeInEaseOut();
											confirmationModal.current?.open();
										}}
										textStyle={font}
										textHeight={24}
									>
										<MaterialCommunityIcons
											name="alert-decagram"
											size={34}
											color="white"
										/>
									</ActionButton.Item>
								)
							)}
						</ActionButton>
					</Fragment>
				);
				break;

			case GROUP_ID_GOODDEAL_ACTIVITY:
			case GROUP_ID_ACTIVITY:
			case GROUP_ID_PROPOSAL:
			case GROUP_ID_TEMPLATE:
				{
					componentButton = (
						<Fragment>
							<ActionButton
								buttonColor={COLOR5}
								renderIcon={() => (
									<MaterialCommunityIcons
										name="dots-horizontal"
										size={40}
										color="white"
									/>
								)}
								verticalOrientation="down"
								size={44}
								offsetX={18}
								offsetY={18}
								degrees={90}
								spacing={10}
								autoInactive={false}
							>
								{Boolean(goodDealId) && (
									<ActionButton.Item
										buttonColor={COLOR2}
										title={t("button:more")}
										disabled={isFetching}
										onPress={() =>
											dispatch(fetchGetAdDetails({ id: goodDealId }))
										}
										textStyle={font}
										textHeight={24}
									>
										{isFetching ? (
											<ActivityIndicator size="small" color="white" />
										) : (
											<MaterialCommunityIcons
												name="dots-horizontal"
												type="material-community"
												size={34}
												color="white"
											/>
										)}
									</ActionButton.Item>
								)}
								{Boolean(url) && url.trim().length > 0 && (
									<ActionButton.Item
										buttonColor={COLOR2}
										title={t("button:more")}
										onPress={() => openBrowser(dispatch, url)}
										textStyle={font}
									>
										<MaterialCommunityIcons
											name="web"
											size={34}
											color="white"
										/>
									</ActionButton.Item>
								)}
								{!isMine && (
									<ActionButton.Item
										buttonColor={COLOR2}
										title={getTitle()}
										onPress={() => {
											if (user && user.role !== ROLE_GUEST) {
												isMounted.current && setFetchingUpdate(true);
												dispatch(
													isInterested
														? fetchUninterested(_id)
														: fetchInterested(_id)
												);
											} else {
												dispatch({
													type: SET_DROP_DOWN_ALERT_WARN,
													warn: "guestforbidden"
												});
											}
										}}
										textStyle={font}
										textHeight={24}
									>
										{fetchingUpdate ? (
											<ActivityIndicator size="small" color="white" />
										) : (
											<MaterialCommunityIcons
												name={isInterested ? "star-outline" : "star-face"}
												size={34}
												color="white"
											/>
										)}
									</ActionButton.Item>
								)}
								{isMine ||
									(!isMine && isInterested && (
										<ActionButton.Item
											buttonColor={COLOR2}
											title={t("button:groupchat")}
											onPress={() => {
												if (user && user.role !== ROLE_GUEST) {
													navigation.navigate("ChatGrpScreen", {
														eventId: eventId?._id || _id
													});
												} else {
													dispatch({
														type: SET_DROP_DOWN_ALERT_WARN,
														warn: "guestforbidden"
													});
												}
											}}
											textStyle={font}
											textHeight={24}
										>
											<Ionicons name={"chatbubbles"} size={34} color="white" />
										</ActionButton.Item>
									))}
								{(isMine || user?.role === ROLE_WEBMASTER) && (
									<ActionButton.Item
										buttonColor="red"
										title={t("button:delete")}
										onPress={() => {
											isMounted.current && setFetchingDelete(true);
											dispatch(fetchDeleteLiveEvent(_id));
											if (isDetails) {
												navigation.goBack();
											}
										}}
										textStyle={font}
										textHeight={24}
										disabled={fetchingDelete}
									>
										{fetchingDelete ? (
											<ActivityIndicator size="small" color="white" />
										) : (
											<MaterialCommunityIcons
												name={"delete-forever"}
												size={34}
												color="white"
											/>
										)}
									</ActionButton.Item>
								)}
							</ActionButton>
						</Fragment>
					);
				}
				break;
			default:
				componentButton =
					isMine || user?.role === ROLE_WEBMASTER ? (
						<Fragment>
							<ActionButton
								buttonColor={COLOR5}
								renderIcon={() => (
									<MaterialCommunityIcons
										name="dots-horizontal"
										size={40}
										color="white"
									/>
								)}
								verticalOrientation="down"
								size={44}
								offsetX={18}
								offsetY={18}
								degrees={90}
								spacing={10}
								autoInactive={false}
							>
								<ActionButton.Item
									buttonColor="red"
									title={t("button:delete")}
									onPress={() => {
										isMounted.current && setFetchingDelete(true);
										dispatch(fetchDeleteLiveEvent(_id));
										if (isDetails) {
											navigation.goBack();
										}
									}}
									textStyle={font}
									textHeight={24}
									disabled={fetchingDelete}
								>
									{fetchingDelete ? (
										<ActivityIndicator size="small" color="white" />
									) : (
										<MaterialCommunityIcons
											name={"delete-forever"}
											size={34}
											color="white"
										/>
									)}
								</ActionButton.Item>
							</ActionButton>
						</Fragment>
					) : null;
		}
		return componentButton;
	};

	const getImageSize = (photoUri, isDetails) => {
		Image.getSize(
			photoUri,
			(width, height) => {
				const proportion = height / width;
				const imageWidth = SCREEN_WIDTH - 40;
				let imageHeight = Math.ceil(imageWidth * proportion);
				if (!isDetails) {
					if (proportion > 1) {
						if (imageHeight > SCREEN_HEIGHT / 2) {
							imageHeight = SCREEN_HEIGHT / 2;
						}
					} else if (imageHeight > SCREEN_HEIGHT / 3) {
						imageHeight = SCREEN_HEIGHT / 3;
					}
				}
				if (isMounted.current) {
					setImageWidth(imageWidth);
					setImageHeight(imageHeight);
				}
			},
			// In case of error, we display image with image height
			() => isMounted.current && setImageHeight(SCREEN_HEIGHT / 3)
		);
	};

	let when,
		where,
		title,
		description,
		postDescription = null;
	const isInterested = Boolean(usersanswers && user && usersanswers[user._id]);
	const fromId = user && user._id === userId ? "" : userId;

	if (event) {
		const { dateId, timeId, locationId, equipmentId, choiceId } = event;
		when = dateId
			? getTranslatedProperty(templateDates[dateId], "name") +
			  (timeId
					? " - " + getTranslatedProperty(templateTimes[timeId], "name")
					: "")
			: null;
		where = locationId
			? getTranslatedProperty(locations[locationId], "name")
			: equipmentId
			? getTranslatedProperty(equipments[equipmentId], "name")
			: null;
		if (choiceId) {
			if (groupId === GROUP_ID_GOODDEAL_ACTIVITY && goodDeal?.choices) {
				description = getTranslatedProperty(
					goodDeal.choices?.[choiceId],
					"description"
				);
				postDescription = getTranslatedProperty(
					goodDeal?.choices?.[choiceId],
					"postDescription"
				);
			} else if (template.choices) {
				description = getTranslatedProperty(
					template.choices?.[choiceId],
					"description"
				);
				postDescription = getTranslatedProperty(
					template.choices?.[choiceId],
					"postDescription"
				);
			}
		} else {
			if (groupId === GROUP_ID_GOODDEAL_ACTIVITY) {
				description = goodDeal
					? getTranslatedProperty(goodDeal, "activityDescription")
					: getTranslatedProperty(event, "activityDescription");
				postDescription = goodDeal
					? getTranslatedProperty(goodDeal, "activityPostDescription")
					: getTranslatedProperty(event, "activityPostDescription");
			} else {
				description = template
					? getTranslatedProperty(template, "description")
					: getTranslatedProperty(event, "description");
				postDescription = template
					? getTranslatedProperty(template, "postDescription")
					: getTranslatedProperty(event, "postDescription");
			}
		}
	}
	if (eventId && !template) {
		title = getTranslatedProperty(eventId, "title");
		description = getTranslatedProperty(eventId, "description");
		postDescription = getTranslatedProperty(eventId, "postDescription");
	} else if (eventTitle) {
		title = getTranslatedProperty(template, "title");
	}

	if (groupId === GROUP_ID_GOODDEAL_ACTIVITY && goodDeal) {
		photoUri = goodDeal.photoUri;
	}

	const displayPhotoUri = Boolean(
		groupId !== GROUP_ID_ACTIVITY && groupId !== GROUP_ID_PROPOSAL && photoUri
	);

	return (
		<View style={[bgWhite, shadowGrey, _id ? mv5 : mb5]}>
			<View style={[ph10, pt10, _id ? borderBottomColor5 : null]}>
				{_id && <LiveHeader item={item} />}
				<ConfirmationModal
					ref={confirmationModal}
					description={t("profile:confirmflag")}
					title={t("menu:confirmation")}
					onConfirmation={flagContent}
					isFetching={fetchingFlagContent}
					buttonConfirm={t("button:flag")}
					buttonColor={ORANGE_COLOR}
				/>
				{isDetails ? (
					<View
						style={[p10, groupId === GROUP_ID_PUBLISH && { minHeight: 250 }]}
					>
						{Boolean(title) && (
							<Text style={[fontBold, uppercase]}>{title}</Text>
						)}

						{groupId === GROUP_ID_NEW_USER && (
							<View>
								<Text style={[font, mb10]}>
									{t("live:welcome", { firstName: users?.[userId]?.firstname })}
								</Text>
								<Avatar
									containerStyle={[
										{
											height: SCREEN_HEIGHT / 4,
											width: SCREEN_WIDTH - 40
										},
										alignSelfCenter,
										rounded10,
										mb10
									]}
									renderPlaceholderContent={
										<Image
											style={[
												{
													height: SCREEN_HEIGHT / 4,
													width: SCREEN_WIDTH - 40
												},
												rounded10
											]}
											source={getImageUri("placeholder")}
										/>
									}
									source={{
										uri: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExaXczYjhlazhkMW43NDl2ZGloY2xjZmxnYnByM3c4YThzbmdjeDRpMSZlcD12MV9naWZzX3NlYXJjaCZjdD1n/ZIkBzupEM9Ql9lWKQz/giphy.gif"
									}}
									overlayContainerStyle={[rounded10]}
								/>
							</View>
						)}

						{groupId === GROUP_ID_ACTIVITY || groupId === GROUP_ID_PROPOSAL ? (
							<View style={[flex1, row, mb10]}>
								<View style={styles.buttonActivity}>
									<Image
										style={[
											{
												width: 50,
												height: 50,
												position: "absolute",
												bottom: 5,
												tintColor: DARKGREY_COLOR
											}
										]}
										source={getImageUri(picto)}
									/>
									<Image
										style={[
											{
												width: 50,
												height: 50,
												position: "absolute",
												bottom: 5
											},
											opacity0
										]}
										source={getImageUri(picto)}
									/>
								</View>
								<Text style={[flex1, mt10, font]}>{description}</Text>
							</View>
						) : (
							<Text style={[font, mb10]}>{description}</Text>
						)}
						{displayPhotoUri &&
							(isPdfUri(photoUri) ? (
								<TouchableOpacity
									style={[
										{
											height: 200,
											width: SCREEN_WIDTH - 40,
											backgroundColor: COLOR5,
											borderRadius: 10,
											justifyContent: "center",
											alignItems: "center",
											marginBottom: 10,
											alignSelf: "center"
										}
									]}
									onPress={() => {
										if (url && url.trim().length > 0) {
											openBrowser(dispatch, url);
										} else {
											navigation.navigate("DocReaderScreen", {
												uri: photoUri,
												sharing: true
											});
										}
									}}
								>
									<MaterialCommunityIcons
										name="file-pdf-box"
										size={80}
										color={COLOR2}
									/>
									<Text style={[font, mt10, color2]}>
										{extractPdfNameFromUri(photoUri) || t("chat:pdfdocument")}
									</Text>
								</TouchableOpacity>
							) : (
								<Avatar
									containerStyle={[
										{ height: imageHeight, width: imageWidth },
										alignSelfCenter,
										rounded10,
										mb10
									]}
									renderPlaceholderContent={
										<Image
											style={[
												{ height: imageHeight, width: imageWidth },
												rounded10
											]}
											source={getImageUri("placeholder")}
										/>
									}
									source={{
										uri: photoUri
									}}
									overlayContainerStyle={[rounded10]}
									onPress={() => {
										if (url && url.trim().length > 0) {
											openBrowser(dispatch, url);
										} else {
											navigation.navigate("ImageZoomScreen", {
												uri: photoUri
											});
										}
									}}
								/>
							))}
						{youtubeVideoId && photoUri && (
							<Tile
								activeOpacity={0.5}
								containerStyle={[
									{
										height: ((SCREEN_WIDTH - 40) * 9) / 16,
										width: SCREEN_WIDTH - 40
									},
									alignSelfCenter,
									rounded10,
									overflowHidden
								]}
								imageSrc={{ uri: photoUri }}
								imageContainerStyle={[
									rounded10,
									{
										height: ((SCREEN_WIDTH - 40) * 9) / 16,
										width: SCREEN_WIDTH - 40
									}
								]}
								icon={{
									name: "play-circle",
									type: "font-awesome",
									size: 50,
									color: COLOR2
								}}
								onPress={() => {
									navigation.navigate("YoutubeScreen", {
										videoId: youtubeVideoId
									});
								}}
								featured
							/>
						)}
						{Boolean(postDescription) && (
							<Text style={[font, mt10, mb10]}>{postDescription}</Text>
						)}
						{Boolean(where) && (
							<View style={[row]}>
								<View style={[flex1]} />
								<Text style={[font, darkgrey]}>{t("live:where")}</Text>
								<Text style={[fontBold]}>{where}</Text>
							</View>
						)}
						{Boolean(startDate || when) && (
							<View style={[row]}>
								<View style={[flex1]} />
								<Text style={[font, darkgrey]}>{t("live:when")}</Text>
								<Text style={[fontBold]}>
									{startDate ? `${formatDate(startDate)}` : when}
								</Text>
							</View>
						)}
						{Boolean(usersanswers && Object.values(usersanswers).length) && (
							<View style={[flex1, mt5, rowReverse]}>
								<Text style={[font, darkgrey]}>
									{groupId === GROUP_ID_PUBLISH
										? `👍 : ${Object.values(usersanswers).length}`
										: Object.values(usersanswers).length > 1
										? `${t("live:answers")} ${
												Object.values(usersanswers).length
										  }`
										: `${t("live:answer")} ${
												Object.values(usersanswers).length
										  }`}
								</Text>
							</View>
						)}
					</View>
				) : (
					<View style={[p10]}>
						{Boolean(title) && (
							<Text style={[fontBold, uppercase]}>{title}</Text>
						)}
						{groupId === GROUP_ID_NEW_USER && (
							<TouchableOpacity
								onPress={() =>
									navigation.navigate("EventInterestedUsersScreen", {
										id: _id
									})
								}
								disabled={Boolean(
									(!_id || !isMine) && user?.role !== ROLE_WEBMASTER
								)}
							>
								<Text style={[font, mb10]}>
									{t("live:welcome", { firstName: users?.[userId]?.firstname })}
								</Text>
								<Text style={[font]}>{t("live:welcome2")}</Text>
							</TouchableOpacity>
						)}
						{Boolean(
							groupId === GROUP_ID_ACTIVITY || groupId === GROUP_ID_PROPOSAL
						) ? (
							<View style={[row, mb10]}>
								<TouchableOpacity
									style={styles.buttonActivity}
									onPress={() =>
										_id &&
										navigation.navigate("EventInterestedUsersScreen", {
											id: _id
										})
									}
									disabled={!Boolean(_id)}
								>
									<Image
										style={{
											width: 50,
											height: 50,
											position: "absolute",
											bottom: 5,
											tintColor: DARKGREY_COLOR
										}}
										source={getImageUri(picto)}
									/>
									<Image
										style={{
											width: 50,
											height: 50,
											bottom: 5,
											position: "absolute",
											opacity: 0
										}}
										source={getImageUri(picto)}
									/>
								</TouchableOpacity>
								<Text style={[flex1, mt10, font]}>{description}</Text>
							</View>
						) : groupId !== GROUP_ID_NEW_USER ? (
							<TouchableOpacity
								onPress={() => {
									_id &&
										navigation.navigate("EventInterestedUsersScreen", {
											id: _id
										});
								}}
							>
								<Text style={[font, mb10]} numberOfLines={title ? 2 : 3}>
									{description}
								</Text>
							</TouchableOpacity>
						) : null}
						{displayPhotoUri &&
							(isPdfUri(photoUri) ? (
								<TouchableOpacity
									style={[
										{
											height: 200,
											width: SCREEN_WIDTH - 40,
											backgroundColor: COLOR5,
											borderRadius: 10,
											justifyContent: "center",
											alignItems: "center",
											marginBottom: 10,
											alignSelf: "center"
										}
									]}
									onPress={() => {
										if (_id && Boolean(user && user.role && !fromId)) {
											navigation.navigate("EventInterestedUsersScreen", {
												id: _id
											});
										} else if (goodDeal) {
											dispatch(fetchGetAdDetails({ id: goodDealId }));
										} else if (url && url.trim().length > 0) {
											openBrowser(dispatch, url);
										} else {
											navigation.navigate("DocReaderScreen", {
												uri: photoUri,
												sharing: true
											});
										}
									}}
								>
									<MaterialCommunityIcons
										name="file-pdf-box"
										size={80}
										color={COLOR2}
									/>
									<Text style={[font, mt10, color2]}>
										{extractPdfNameFromUri(photoUri) || t("chat:pdfdocument")}
									</Text>
								</TouchableOpacity>
							) : (
								<Avatar
									containerStyle={[
										{
											height: imageHeight,
											width: imageWidth
										},
										alignSelfCenter,
										rounded10,
										mb10
									]}
									renderPlaceholderContent={
										<Image
											style={[
												{
													height: imageHeight,
													width: imageWidth
												},
												rounded10
											]}
											source={getImageUri("placeholder")}
										/>
									}
									source={{
										uri: photoUri
									}}
									overlayContainerStyle={[rounded10]}
									onPress={() => {
										if (_id && Boolean(user && user.role && !fromId)) {
											navigation.navigate("EventInterestedUsersScreen", {
												id: _id
											});
										} else if (goodDeal) {
											dispatch(fetchGetAdDetails({ id: goodDealId }));
										} else if (url && url.trim().length > 0) {
											openBrowser(dispatch, url);
										} else {
											navigation.navigate("ImageZoomScreen", {
												uri: photoUri
											});
										}
									}}
								/>
							))}
						{youtubeVideoId && photoUri && (
							<Tile
								activeOpacity={0.5}
								containerStyle={[
									{
										height: ((SCREEN_WIDTH - 40) * 9) / 16,
										width: SCREEN_WIDTH - 40
									},
									alignSelfCenter,
									rounded10,
									overflowHidden
								]}
								imageSrc={{ uri: photoUri }}
								imageContainerStyle={[
									rounded10,
									{
										height: ((SCREEN_WIDTH - 40) * 9) / 16,
										width: SCREEN_WIDTH - 40
									}
								]}
								icon={{
									name: "play-circle",
									type: "font-awesome",
									size: 50,
									color: COLOR2
								}}
								onPress={() => {
									Boolean(user && user.role && !fromId && _id)
										? navigation.navigate("EventInterestedUsersScreen", {
												id: _id
										  })
										: navigation.navigate("YoutubeScreen", {
												videoId: youtubeVideoId
										  });
								}}
								featured
							/>
						)}
						{Boolean(postDescription) && (
							<Text style={[font, mt10, mb10]}>{postDescription}</Text>
						)}
						{Boolean(where) && (
							<View style={[row]}>
								<View style={[flex1]} />
								<Text style={[font, darkgrey]}>{t("live:where")}</Text>
								<Text style={[fontBold, darkgrey]}>{where}</Text>
							</View>
						)}
						{Boolean(startDate || when) && (
							<View style={[row]}>
								<View style={[flex1]} />
								<Text style={[font, darkgrey]}>{t("live:when")}</Text>
								<Text style={[fontBold, darkgrey]}>
									{startDate ? `${formatDate(startDate)}` : when}
								</Text>
							</View>
						)}
						{Boolean(usersanswers && Object.values(usersanswers).length) && (
							<View style={[flex1, mt5, alignItemsCenter, rowReverse]}>
								<Text style={[font, darkgrey]}>
									{groupId === GROUP_ID_PUBLISH
										? `👍 : ${Object.values(usersanswers).length}`
										: Object.values(usersanswers).length > 1
										? `${t("live:answers")} ${
												Object.values(usersanswers).length
										  }`
										: `${t("live:answer")} ${
												Object.values(usersanswers).length
										  }`}
								</Text>
							</View>
						)}
					</View>
				)}
				{_id && renderButtons()}
			</View>
			{Boolean(_id) && (
				<View style={[flex1, row]}>
					<View style={[flex1, borderRightColor5]}>
						{Boolean(
							groupId !== GROUP_ID_PUBLISH &&
								groupId !== GROUP_ID_NEW_USER &&
								subtype !== "Post"
						) && (
							<Button
								type="clear"
								icon={
									<Ionicons name={"chatbubbles"} size={15} color={COLOR2} />
								}
								titleStyle={[fontBold, color2, fs12, { paddingLeft: 5 }]}
								containerStyle={[{ alignSelf: "flex-start" }]}
								title={t("button:groupchat")}
								onPress={() => {
									if (user && user.role !== ROLE_GUEST) {
										navigation.navigate("ChatGrpScreen", {
											eventId: eventId?._id || _id
										});
									} else {
										dispatch({
											type: SET_DROP_DOWN_ALERT_WARN,
											warn: "guestforbidden"
										});
									}
								}}
							/>
						)}
					</View>
					<View style={[flex1]}>
						{Boolean(
							(fromId && groupId !== GROUP_ID_NEW_USER) ||
								(fromId && groupId === GROUP_ID_NEW_USER && !isInterested)
						) && (
							<Button
								type="clear"
								titleStyle={[
									fontBold,
									isInterested ? red : color2,
									fs12,
									{ paddingLeft: 5 }
								]}
								icon={
									groupId === GROUP_ID_NEW_USER ? null : groupId ===
											GROUP_ID_PUBLISH || subtype === "Post" ? (
										<AntDesign
											name={isInterested ? "dislike" : "like"}
											size={15}
											color={isInterested ? "red" : COLOR2}
										/>
									) : (
										<MaterialCommunityIcons
											name={isInterested ? "star-outline" : "star-face"}
											size={18}
											color={isInterested ? "red" : COLOR2}
										/>
									)
								}
								containerStyle={[{ alignSelf: "flex-end", paddingRight: 5 }]}
								title={
									groupId === GROUP_ID_NEW_USER
										? t("button:welcome")
										: isInterested
										? groupId === GROUP_ID_PUBLISH || subtype === "Post"
											? t("button:dislike")
											: t("button:removeanswer")
										: groupId === GROUP_ID_PUBLISH || subtype === "Post"
										? t("button:like")
										: t("button:answerme")
								}
								loading={fetchingUpdate}
								onPress={() => {
									if (user && user.role !== ROLE_GUEST) {
										isMounted.current && setFetchingUpdate(true);
										dispatch(
											isInterested
												? fetchUninterested(_id)
												: fetchInterested(_id)
										);
									} else {
										dispatch({
											type: SET_DROP_DOWN_ALERT_WARN,
											warn: "guestforbidden"
										});
									}
								}}
							/>
						)}
					</View>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	buttonActivity: {
		height: 60,
		width: 60,
		borderColor: DARKGREY_COLOR,
		borderRadius: 3,
		backgroundColor: "white",
		borderWidth: 1,
		alignItems: "center",
		justifyContent: "space-evenly",
		margin: 5
	}
});

const MemoizedLiveEvent = memo(LiveEvent);
export default MemoizedLiveEvent;
